import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { WidgetFilterKey } from "@app/dashboard/shared/models/WidgetFilterKey";
import { FindingReportDataService } from "@app/findingreport/services";
import { Pages } from "@constants/pages/Pages";
import { ComparisonType } from "@enums/workspace/ComparisonType";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { BaseHighchartWidget } from "../BaseHighchartWidget";
import { firstValueFrom } from "rxjs";
import { provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "finding-report-widget",
    templateUrl: "../highchart-widget.component.html",
    styleUrls: ["../highchart-widget.component.scss"],
    providers: [provideTranslocoScope("dashboard")]
})
export class FindingReportWidgetComponent extends BaseHighchartWidget {
    @ViewChild("chartContainer") chartContainer: ElementRef;

    private ds = inject(FindingReportDataService);
    private pages = inject(Pages);

    constructor() {
        super();

        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.Organization] =
            "organizationId";
    }

    protected async getChartConfig() {
        await this.preloadDashboardScope();

        const filters = this.getFilters();
        const hasReportFilter = filters[this.filterKeys.FindingsReport.ReportType];
        if (!hasReportFilter) {
            this.config.noData = true;
            this.config.noDataExplanation = this.transloco.translate(
                "dashboard.findingReportWidget.reportTypeRequired"
            );
            return null;
        }

        const data = await firstValueFrom(this.ds.getWidgetInfo(filters));

        this.config.noData = !data || data.length === 0;
        if (this.config.noData) {
            return null;
        }

        const config = this.getBaseChartConfig(null, [
            <any>{
                colorByPoint: true,
                data,
            },
        ]);

        config.tooltip = {
            headerFormat: "<b>{point.key}:</b> {point.y}",
            pointFormat: "",
        };

        config.legend = { enabled: false };
        config.xAxis = {
            type: "category",
        };

        config.yAxis = {
            min: 0,
            allowDecimals: false,
            title: {
                text: this.transloco.translate("dashboard.findingReportWidget.yAxisTitle"),
            },
        };

        return config;
    }

    protected async openWorkspace(ev) {
        const searchKeywords: SearchKeyword[] = await this.getSearchKeywords(ev.point.name);

        const preset = await this.getBasePreset(searchKeywords);
        this.openPreset(this.pages.ImOverview, preset);
    }

    private async getSearchKeywords(score: string) {
        const searchKeywords: SearchKeyword[] = [];

        const reportId = +this.getFilter(this.filterKeys.FindingsReport.ReportType);
        if (reportId > 0) {
            const reportTypeName = (await firstValueFrom(this.ds.getReportTypes())).find(
                (x) => x.value === reportId
            ).label;

            searchKeywords.push(
                this.getSearchKeyword("reportType", reportTypeName, ComparisonType.Contains)
            );
        }

        searchKeywords.push(this.getSearchKeyword("overallScore", score, ComparisonType.Contains));

        return searchKeywords;
    }

    protected async onGlobalOpenClick() {
        const preset = await this.getBasePreset();
        this.openPreset(this.pages.ImOverview, preset);
    }
}
